// Tema claro/escuro por `data-theme` (tokens em packages/ui/theme.css); preferência só em localStorage.
import { createContext, useContext } from 'react';

export const THEMES = ['light', 'dark', 'system'] as const;
export type Theme = (typeof THEMES)[number];
export type ResolvedTheme = Exclude<Theme, 'system'>;

export const THEME_STORAGE_KEY = 'giyus.theme';

export interface ThemeContextValue {
  readonly theme: Theme;
  readonly resolved: ResolvedTheme;
  readonly setTheme: (theme: Theme) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

export interface ThemeHost {
  readonly storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  readonly prefersDark: () => boolean;
  readonly apply: (resolved: ResolvedTheme) => void;
}

export function browserThemeHost(): ThemeHost {
  return {
    storage: typeof localStorage === 'undefined' ? null : localStorage,
    prefersDark: () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches,
    apply: (resolved) => {
      document.documentElement.dataset['theme'] = resolved;
      document.documentElement.classList.toggle('dark', resolved === 'dark');
    },
  };
}

export function readStoredTheme(storage: ThemeHost['storage']): Theme {
  try {
    const stored = storage?.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

export function resolveTheme(theme: Theme, prefersDark: boolean): ResolvedTheme {
  if (theme === 'system') {
    return prefersDark ? 'dark' : 'light';
  }
  return theme;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) {
    throw new Error('useTheme must be used within <ThemeProvider>.');
  }
  return value;
}
